import { type ElementType, type FC } from 'react';
import styled from 'styled-components';

import { StyledLink } from '@xing-com/crate-layout-nav-item-styled-link';
import { useActiveItem, useNotifications } from '@xing-com/hub';
import { IconWithBadge } from '@xing-com/platform-layout-icon-with-badge';
import { addParams } from '@xing-com/xing-tracking';

import { IconComponentContextProvider } from './notification-center-icon';

type NavItemHeaderProps = {
  icon: ElementType<{ className?: string }>;
  provideIconContext?: boolean;
  activeIcon: ElementType<{ className?: string }>;
  href: string;
  onClick?: (event: React.MouseEvent) => void;
  notificationKey?: string;
  title: string;
  activeItemValues?: string[];
  tracking?: Record<string, string>;
};

export const StyledTitle = styled.p`
  margin: 0;
  text-decoration: none;
  text-align: left;
  white-space: nowrap;
`;

export const NavItemHeader: FC<NavItemHeaderProps> = ({
  icon,
  provideIconContext,
  activeIcon,
  href,
  onClick,
  notificationKey,
  title,
  activeItemValues,
  tracking,
}) => {
  const activeItem = useActiveItem();
  const notifications = useNotifications();
  const url = href;

  const isActive =
    activeItem && activeItemValues
      ? activeItemValues.includes(activeItem)
      : false;
  const notificationCount =
    notifications && notifications[notificationKey || ''];

  // When active, use regular icon as fallback if no activeIcon is specified
  const IconElement = isActive && activeIcon ? activeIcon : icon;

  const to = tracking ? addParams(url, tracking) : url;

  const inner = (
    <StyledLink
      $isActive={isActive}
      data-testid={`frame-vnav-${activeItemValues?.join('_')}`}
      to={to}
      onClick={onClick}
    >
      <IconWithBadge
        icon={IconElement}
        title={title}
        count={notificationCount}
      />
      <StyledTitle>{title}</StyledTitle>
    </StyledLink>
  );

  return provideIconContext ? (
    <IconComponentContextProvider value={{ count: notificationCount || 0 }}>
      {inner}
    </IconComponentContextProvider>
  ) : (
    inner
  );
};
